import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from './queryDeduplication';

// Cache for level thresholds to avoid repeated queries
let levelThresholdsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches level thresholds from the database
 * Uses caching and request deduplication to minimize database queries
 */
export async function getLevelThresholds() {
  const now = Date.now();
  
  // Return cached data if still valid
  if (levelThresholdsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return levelThresholdsCache;
  }

  // Use request deduplication to prevent duplicate simultaneous requests
  const cacheKey = getCacheKey('getLevelThresholds', []);
  const data = await dedupeRequest(cacheKey, async () => {
    try {
      const { data, error } = await supabase
        .from('xp_levels')
        .select('level, min_xp')
        .order('level', { ascending: true });

      if (error) {
        console.error('Error fetching level thresholds:', error);
        return null;
      }

      // Cache the results
      levelThresholdsCache = data;
      cacheTimestamp = Date.now();
      
      return data;
    } catch (error) {
      console.error('Error in getLevelThresholds:', error);
      return null;
    }
  });

  return data;
}

/**
 * Calculates the user's level based on their XP
 * @param {number} xp - User's total XP
 * @returns {number} - User's current level
 */
export async function calculateLevel(xp) {
  const thresholds = await getLevelThresholds();
  
  if (!thresholds || thresholds.length === 0) {
    // Fallback to level 1 if no thresholds found
    return 1;
  }

  // Find the highest level where min_xp <= user's XP
  let userLevel = 1;
  for (const threshold of thresholds) {
    if (threshold.min_xp <= xp) {
      userLevel = threshold.level;
    } else {
      break;
    }
  }

  return userLevel;
}

/**
 * Calculates progress to next level
 * @param {number} xp - User's total XP
 * @param {number} currentLevel - User's current level (optional, will be calculated if not provided)
 * @returns {Object} - { currentLevel, nextLevel, currentLevelXP, nextLevelXP, progress, xpNeeded }
 */
export async function calculateLevelProgress(xp, currentLevel = null) {
  const thresholds = await getLevelThresholds();
  
  if (!thresholds || thresholds.length === 0) {
    return {
      currentLevel: 1,
      nextLevel: null,
      currentLevelXP: 0,
      nextLevelXP: null,
      progress: 0,
      xpNeeded: null
    };
  }

  // Calculate current level if not provided
  if (currentLevel === null) {
    currentLevel = await calculateLevel(xp);
  }

  // Find current and next level thresholds
  const currentThreshold = thresholds.find(t => t.level === currentLevel);
  const nextThreshold = thresholds.find(t => t.level === currentLevel + 1);

  if (!currentThreshold) {
    return {
      currentLevel: 1,
      nextLevel: null,
      currentLevelXP: 0,
      nextLevelXP: null,
      progress: 0,
      xpNeeded: null
    };
  }

  const currentLevelXP = currentThreshold.min_xp;
  
  // If there's no next level, user is at max level
  if (!nextThreshold) {
    return {
      currentLevel,
      nextLevel: null,
      currentLevelXP,
      nextLevelXP: null,
      progress: 1, // 100% progress at max level
      xpNeeded: null
    };
  }

  const nextLevelXP = nextThreshold.min_xp;
  const xpInCurrentLevel = xp - currentLevelXP;
  const xpNeededForNextLevel = nextLevelXP - currentLevelXP;
  const progress = Math.min(xpInCurrentLevel / xpNeededForNextLevel, 1);
  const xpNeeded = nextLevelXP - xp;

  return {
    currentLevel,
    nextLevel: nextThreshold.level,
    currentLevelXP,
    nextLevelXP,
    progress,
    xpNeeded
  };
}

/**
 * Clears the level thresholds cache (useful after level thresholds are updated)
 */
export function clearLevelThresholdsCache() {
  levelThresholdsCache = null;
  cacheTimestamp = null;
}

